import translateFromDBValue from "@/mixins/translateFromDBValue";
import propertyFilter from "@/mixins/propertyFilters";
import {downloadCloudFilesArchive, getDebtDTI, getIncomeDTI, getPropertiesByIds} from "@/services/brokerService";
import store from "../store/index"


export default {
  methods: {
    async downloadFilesAsZip(files, nameOfFile) {
      const uniqueFileIds = [...new Set((files || []).map((file) => file.id).filter(Boolean))];
      if (uniqueFileIds.length === 0) {
        throw new Error("ZIP_EMPTY");
      }
      const zipBlob = await downloadCloudFilesArchive(uniqueFileIds);
      this.downloadZip(zipBlob, nameOfFile.endsWith(".zip") ? nameOfFile : `${nameOfFile}.zip`);
    },
    downloadZip(blob, nameOfFile) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nameOfFile;
      a.target = "_blank";
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    getCloudFileTypeByExtension(fileName) {
      const fileExtension = fileName.match(/\.[0-9a-z]+$/i)?.[0]?.toLowerCase();

      switch (fileExtension) {
        case ".pdf":
          return "application/pdf";
        case ".xlsx":
          return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        case ".png":
          return "image/png";
        case ".jpg":
        case ".jpeg":
          return "image/jpeg";
        case ".doc":
          return "application/msword";
        case ".docx":
          return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        case ".zip":
          return "application/zip";
        case ".asice":
          return "application/vnd.etsi.asic-e+zip"; // oficiálny MIME typ pre .asice
        default:
          return "application/octet-stream"; // fallback pre neznáme typy
      }
    },
    collectUploadedFilesFromMortgageCloud() {
      const cloud = store.getters["mortgageDetailCloud/getClouds"];
      let uploadedCloudFiles = [];
      for (const cloudElement of cloud) {
        const cloudMeta = cloudElement.cloudMeta?._?.clouds?.[0];
        if (cloudElement.invisible || cloudMeta?.invisible || cloudMeta?.isActive === false) continue;
        if (!cloudElement.missingFiles) continue;
        for (const cloudMissingFiles of cloudElement.missingFiles) {
          if (!cloudMissingFiles.uploaded) continue;
          uploadedCloudFiles = [...uploadedCloudFiles, ...cloudMissingFiles.uploaded];
        }
      }
      return uploadedCloudFiles.filter((file, index, files) => (
        files.findIndex((candidate) => candidate.id === file.id) === index
      ));
    },
    async constructAllRelatedCloudsToMortgage(mortgage, mortgageId="_") {
      const applicantInfo = {
        clientType: "žiadateľ",
        firstName: mortgage.applicant.first_name,
        lastName: mortgage.applicant.last_name
      }
      const coapplicantInfo = {
        clientType: "spolužiadateľ",
        firstName: mortgage.coapplicant?.first_name,
        lastName: mortgage.coapplicant?.last_name
      }
      let incomesArray = [
        ...this.mergeClientInfoToObjects(mortgage.applicant.incomes, applicantInfo),
        ...this.mergeClientInfoToObjects(mortgage.coapplicant?.incomes, coapplicantInfo),
      ].filter((income) => income.is_active);
      incomesArray = await this.calculateDtiToIncomes(incomesArray);

      let debtsArray = [
        ...this.mergeClientInfoToObjects(mortgage.applicant.debts, applicantInfo),
        ...this.mergeClientInfoToObjects(mortgage.coapplicant?.debts, coapplicantInfo),
      ].filter((debt) => (
        debt.mortgage_plan === "keep"
        || debt.mortgage_plan === "refinance"
        || (!debt.mortgage_plan && (debt.is_active || debt.refinancovany_hypotekou === mortgage.id))
      ));
      debtsArray = await this.calculateDtiToDebts(debtsArray);

      let propertiesArray = [];
      if (mortgage.zakladane_nehnutelnosti.length > 0) {
        propertiesArray = (await getPropertiesByIds(mortgage.zakladane_nehnutelnosti)).results;
      }

      const mortgageClouds = this.constructMortgage(mortgage, mortgageId);
      const incomesClouds = this.constructIncomes(incomesArray, mortgageId);
      const debtsClouds = this.constructDebts(debtsArray, mortgageId);
      const propertiesClouds = this.constructProperties(propertiesArray, mortgageId);
      const applicationsClouds = this.constructApplications(mortgage.applications, mortgageId)
      const clientsClouds = [...this.constructClient(mortgage.applicant, true, mortgageId)];
      if (mortgage.coapplicant?.id) {
        clientsClouds.push(...this.constructClient(mortgage.coapplicant, false, mortgageId));
      }
      return [...mortgageClouds, ...incomesClouds, ...debtsClouds, ...propertiesClouds, ...clientsClouds, ...applicationsClouds];
    },
    constructClient(client, applicant, mortgageId="_") {
      if (!client.id) return [];
      return [{
        type: "client",
        cloudId: client.cloud,
        objectId: client.id,
        label: client.first_name ? client.first_name + " " + client.last_name : client.id,
        image: applicant ? require("@/images/avatar_applicant.png") : require("@/images/avatar_coapplicant.png"),
        subField: false,
        cloudMeta: {
          [mortgageId]: {
            clouds:[{
              isActive: true,
              invisible: false,
              id: client.cloud,
              type_label: `Cloud ${applicant ? "žiadateľa" : "spolužiadateľa"}`,
              label: `${client.first_name} ${client.last_name}`,
              files: [],
            }]
          },
        },
        missingFiles: [],
      }]
    },
    constructDebts(debts, mortgageId="_") {
      if (debts.length === 0) return [];
      const translator = translateFromDBValue.methods;
      const clouds = []
      for (const debt of debts) {
        const bank = store.getters["formStore/getBankById"](debt.banka);
        const cisloUveruLabel = `č. ${debt.cislo_uveru ?? "neznáme"}`;
        const cloudLabel = `${translator.translateDebtType(debt.typ).toLowerCase()} ${debt.clientInfo.lastName} (${bank?.name ? bank.name + " " : ""}${cisloUveruLabel})`
        clouds.push({
          type: "debt",
          cloudId: debt.cloud,
          objectId: debt.id,
          label: "Výdavok " + debt.clientInfo.type.toLowerCase() + "a",
          image: require("@/images/debt icon.png"),
          subField: true,
          subLabel: cloudLabel,
          subValue: debt.dti.value,
          cloudMeta: {
            [mortgageId]: {
              clouds:[{
                isActive: debt.mortgage_plan
                  ? debt.mortgage_plan !== "repay"
                  : debt.is_active,
                invisible: false,
                id: debt.cloud,
                type_label: `Cloud výdavku ${cloudLabel}`,
                label: debt.dti.value + "€",
                files: [],
              }]
            },
          },
          missingFiles: [],
        })
      }
      return clouds
    },
    constructMortgage(mortgage, mortgageId="_") {
      if (!mortgage.id) return [];
      return [{
        type: "mortgage",
        cloudId: mortgage.cloud,
        objectId: mortgage.id,
        label: "Dokladovanie účelu",
        image: require("@/images/dokladovanie_ucelu_avatar.png"),
        invisible: false,
        cloudMeta: {
          [mortgageId]: {
            clouds:[{
              invisible: false,
              isActive: true,
              type_label: "Cloud hypotéky",
              label: ``,
              id: mortgage.cloud,
              files: [],
            }]
          },
        },
        missingFiles: [],
      }]
    },
    constructApplications(applications, mortgageId="_") {
      if (applications.length === 0) return [];
      const clouds = [];
      for (const application of applications) {
        clouds.push({
          type: "application",
          cloudId: application.cloud,
          objectId: application.id,
          objectData: {
            stage: application.stage,
          },
          invisible: true,
          cloudMeta: {
            [mortgageId]: {
              clouds:[{
                invisible: true,
                type_label: "Cloud žiadosti",
                label: `${application.bank.nazov}`,
                id: application.cloud,
                files: [],
              }]
            },
          },
          missingFiles: [],
        })
      }
      return clouds
    },
    constructProperties(properties, mortgageId="_", usedProperties=null) {
      if (properties.length === 0) return [];
      const clouds = [];
      for (const property of properties) {
        clouds.push({
          type: "property",
          cloudId: property.cloud,
          objectId: property.id,
          label: "Nehnuteľnosť",
          image: require("@/images/property-broker-app-icon.png"),
          subField: true,
          subLabel: propertyFilter.methods.getAddressModified(property),
          subValue: property.hodnota,
          cloudMeta: {
            [mortgageId]: {
              clouds:[{
                isActive: usedProperties ? usedProperties.includes(property.id) : true,
                invisible: false,
                id: property.cloud,
                type_label: `Cloud nehnuteľnosti`,
                label: propertyFilter.methods.getAddressModified(property),
                files: [],
              }]
            },
          },
          missingFiles: [],
        })
      }
      return clouds
    },
    constructIncomes(incomes, mortgageId="_") {
      if (incomes.length === 0) return [];
      const translator = translateFromDBValue.methods;
      const clouds = []
      for (const income of incomes) {
        clouds.push({
          type: "income",
          cloudId: income.cloud,
          objectId: income.id,
          label: "Príjem " + income.clientInfo.type.toLowerCase() + "a",
          image: require("@/images/incomeitemcloud.png"),
          subField: true,
          subLabel: [
            translator.translateIncomeType(income.druh),
            income.zamestnavatel?.name,
          ].filter(Boolean).join(" · "),
          subValue: income.dti.value,
          cloudMeta: {
            [mortgageId]: {
              clouds:[{
                isActive: income.is_active,
                invisible: false,
                id: income.cloud,
                type_label: `Cloud príjmu ${translator.translateIncomeType(income.druh).toLowerCase()} ${income.clientInfo.lastName} (${income.zamestnavatel?.name ?? "neznámy zamestnávateĺ/spoločnosť" })`,
                label: income.dti.value + "€",
                files: [],
              }]
            },
          },
          missingFiles: [],
        })
      }
      return clouds
    },
    async calculateDtiToIncomes(incomes) {
      const promises = [];
      for (const income of incomes) {
        promises.push({func: getIncomeDTI, args: income.id});
      }
      const incomesDtiInfo = await Promise.all(promises.map((promise) => promise.func(promise.args, true)));
      return incomes.map((income) => {
        const dtiObject = incomesDtiInfo.find((dtiObject) => dtiObject.id === income.id);
        return {
          ...income,
          dti: this.pickHighestDti(dtiObject).dtiMaxObject
        };
      });
    },
    async calculateDtiToDebts(debts) {
      const promises = [];
      for (const debt of debts) {
        promises.push({func: getDebtDTI, args: debt.id});
      }
      const debtsDtiInfo = await Promise.all(promises.map((promise) => promise.func(promise.args, true)));
      return debts.map((debt) => {
        const dtiObject = debtsDtiInfo.find((dtiObject) => dtiObject.id === debt.id);
        return {
          ...debt,
          dti: this.pickHighestDti(dtiObject).dtiMaxObject
        };
      });
    },
    pickHighestDti (dtiInfo) {
      if (!dtiInfo) return 0;
      let dtiMax = Number.MIN_SAFE_INTEGER;
      let dtiMaxObject = {};
      const incomeDtiData = Object.values(dtiInfo.data);
      for (const dtiItem of incomeDtiData) {
        if (dtiItem.success && dtiMax < dtiItem.value) {
          dtiMax = dtiItem.value;
          dtiMaxObject = dtiItem;
        }
      }
      if (dtiMax === Number.MIN_SAFE_INTEGER) dtiMax = null;
      let error = null;
      if (!dtiMax) {
        error = incomeDtiData[1].details;
        dtiMaxObject.value = 0;
      }
      return {dtiMaxObject, error};
    },
    mergeClientInfoToObjects(objects, {clientType, firstName, lastName}) {
      if (!objects) return [];
      for (const object of objects) {
        object.clientInfo = {type: clientType, firstName, lastName};
      }
      return objects;
    },
    filterInvisibleClouds(clouds) {
      return clouds.filter((cloud) => !cloud.invisible);
    }
  }
}
